import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Product } from 'npx-family-happy-common';
import { Observable, Subject, takeUntil } from 'rxjs';
import { B2BCompanyConventionService } from '../b2b-company-convention.service';
import { AREAS, CatalogFHService, errorMessage, lineOf } from '../catalog.model';
import { Convention, ConventionRule, PRICING_MODES, PricingMode, pricingUnitOf, ruleFor } from '../convention.model';

/** Solo per la tariffa oraria: massimo vuoto = tariffa unica, altrimenti non sotto il minimo. */
function rangeValidator(group: AbstractControl): ValidationErrors | null {
  if (group.get('pricingMode')?.value !== 'HOURLY') return null;
  const min = group.get('minPrice')?.value;
  const max = group.get('maxPrice')?.value;
  if (min === null || min === '' || max === null || max === '') return null;
  return +max < +min ? { range: true } : null;
}

/**
 * Prezzo di un prodotto B2B nella convenzione dell'azienda: a prestazione (selezione nel caregiving,
 * seduta negli altri servizi) con un costo fisso, oppure orario con un intervallo min–max, la cui
 * tariffa esatta si definisce sulla singola richiesta. Più la quantità compresa.
 */
@Component({
  selector: 'app-catalog-rule-modal',
  templateUrl: './catalog-rule-modal.component.html',
  styleUrls: ['./catalog-rule-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class CatalogRuleModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) companyId!: string;
  @Input({ required: true }) service!: CatalogFHService;
  @Input({ required: true }) product!: Product;
  /** La convenzione dell'azienda: va creata prima (con le date), dal riquadro Convenzione. */
  @Input({ required: true }) convention!: Convention;
  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  readonly modes = PRICING_MODES;
  readonly lineOf = lineOf;

  rule: ConventionRule | null = null;
  saving = false;
  formInvalid = false;
  confirmRemove = false;
  errorText = '';

  /** Importi in euro nel form, in centesimi verso il backend. */
  form = new UntypedFormGroup(
    {
      pricingMode: new UntypedFormControl('SINGLE', [Validators.required]),
      minPrice: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
      maxPrice: new UntypedFormControl(null, [Validators.min(0)]),
      quantity: new UntypedFormControl(null, [Validators.min(0)]),
    },
    { validators: rangeValidator }
  );

  private unsubscribe = new Subject<void>();

  constructor(private conventions: B2BCompanyConventionService) {}

  get tone() {
    return AREAS[lineOf(this.service.businessLine).area].tone;
  }

  get caregiving() {
    return lineOf(this.service.businessLine).area === 'caregiving';
  }

  get hourly() {
    return this.form.get('pricingMode')?.value === 'HOURLY';
  }

  /** Costo zero (a ore: minima e massima zero): le quantità sono gratuite, altrimenti scontate. */
  get freePrice(): boolean {
    const { minPrice, maxPrice } = this.form.value;
    const zero = (x: unknown) => x === null || x === '' || +(x as number) === 0;
    return minPrice !== null && minPrice !== '' && +minPrice === 0 && (!this.hourly || zero(maxPrice));
  }

  /** Unità della modalità scelta (ora, selezione o seduta). */
  get unit() {
    return this.unitOf(this.form.get('pricingMode')?.value);
  }

  unitOf(mode: PricingMode) {
    return pricingUnitOf(mode, this.caregiving);
  }

  ngOnInit(): void {
    this.rule = ruleFor(this.convention, this.product.id);
    if (this.rule) {
      this.form.patchValue({
        pricingMode: this.rule.pricingMode,
        minPrice: this.toEuro(this.rule.minPrice),
        maxPrice:
          this.rule.pricingMode !== 'HOURLY' || this.rule.maxPrice === this.rule.minPrice ? null : this.toEuro(this.rule.maxPrice),
        quantity: this.rule.quantity || null,
      });
    } else {
      // Le linee di assistenza in casa si pagano a ore, le altre a prestazione.
      const hourly = this.caregiving && String(this.service.businessLine) !== 'CAREGIVER_CONSULT';
      this.form.patchValue({ pricingMode: hourly ? 'HOURLY' : 'SINGLE' });
    }
  }

  /** A prestazione il massimo non c'è: si svuota. */
  setMode(mode: PricingMode) {
    this.form.patchValue(mode === 'HOURLY' ? { pricingMode: mode } : { pricingMode: mode, maxPrice: null });
  }

  submit() {
    this.errorText = '';
    if (this.form.invalid) {
      this.formInvalid = true;
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;
    const minPrice = this.toCents(value.minPrice);
    const maxPrice = value.pricingMode === 'HOURLY' ? this.toCents(value.maxPrice) ?? minPrice : null;
    const quantity = value.quantity === null || value.quantity === '' ? null : Math.round(+value.quantity);
    const rule: ConventionRule = { productId: this.product.id, pricingMode: value.pricingMode, minPrice, maxPrice, quantity };
    this.run(this.conventions.saveRule(this.convention, rule), 'Prezzo in convenzione salvato');
  }

  remove() {
    if (!this.rule) return;
    if (!this.confirmRemove) {
      this.confirmRemove = true;
      return;
    }
    this.run(this.conventions.removeRule(this.convention, this.product.id), 'Prodotto tolto dalla convenzione');
  }

  private toEuro(cents: number | null | undefined) {
    return cents === null || cents === undefined ? null : cents / 100;
  }

  private toCents(euro: unknown): number | null {
    return euro === null || euro === undefined || euro === '' ? null : Math.round(+euro * 100);
  }

  private run(request: Observable<unknown>, message: string) {
    this.saving = true;
    request.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(message);
      },
      error: (error) => {
        this.saving = false;
        this.confirmRemove = false;
        this.errorText = errorMessage(error, 'Impossibile salvare la convenzione.');
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
